import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ContentJourneyService,
  STUDIO_CONTENT_SEARCH,
  StudioContentSearchResult,
  formatDurationSeconds,
} from "@/lib/api/journey.service";
import {
  compatConfig,
  compatOrder,
  computeCompatibility,
  type CompatibilityLevel,
  type TagOptionRef,
} from "@/lib/compatibility";
import { getLanguageLabel } from "@/lib/studio-constants";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Clock,
  FileText,
  Filter,
  Globe,
  Music,
  Plus,
  Search,
  Video,
} from "lucide-react";
import { useState } from "react";

export type { CompatibilityLevel } from "@/lib/compatibility";
export type SearchResult = StudioContentSearchResult;

const contentTypeIcons: Record<string, React.ElementType> = {
  audio: Music,
  video: Video,
  blog: FileText,
  workbook: BookOpen,
};

interface ContentSearchDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayNumber: number;
  dayId: string;
  journeyTagOptionIds: string[];
  journeyCategoryId: string | null;
  allContentIds: string[];
  primaryLanguage?: string;
  selectedLanguages?: string[];
  allTagOptions?: TagOptionRef[];
  onAddContent: (dayId: string, result: StudioContentSearchResult) => void;
  onAddContentWithWarning?: (
    dayId: string,
    result: StudioContentSearchResult,
    level: CompatibilityLevel,
  ) => void;
}

export function ContentSearchDrawer({
  open,
  onOpenChange,
  dayNumber,
  dayId,
  journeyTagOptionIds,
  journeyCategoryId,
  allContentIds,
  primaryLanguage,
  selectedLanguages,
  allTagOptions = [],
  onAddContent,
  onAddContentWithWarning,
}: ContentSearchDrawerProps) {
  const effectiveLanguages =
    selectedLanguages && selectedLanguages.length > 0
      ? selectedLanguages
      : primaryLanguage
        ? [primaryLanguage]
        : ["en"];
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<string>("all");
  const [previewId, setPreviewId] = useState<string | null>(null);

  const searchParamsStr = (() => {
    const p = new URLSearchParams();
    if (searchQuery) p.set("q", searchQuery);
    if (searchType !== "all") p.set("type", searchType);
    if (effectiveLanguages.length > 0)
      p.set("languages", effectiveLanguages.join(","));
    return p.toString();
  })();

  const { data: searchResults = [], isLoading: isSearching } = useQuery<
    StudioContentSearchResult[]
  >({
    queryKey: [STUDIO_CONTENT_SEARCH, searchParamsStr],
    queryFn: () => ContentJourneyService.searchStudioContent(searchParamsStr),
    enabled: open,
  });

  const rankedResults = [...searchResults]
    .map((result) => ({
      ...result,
      compatibility: computeCompatibility(
        result.tagOptionIds || [],
        journeyTagOptionIds,
        result.categoryId,
        journeyCategoryId,
        allTagOptions,
      ),
    }))
    .sort(
      (a, b) => compatOrder[a.compatibility] - compatOrder[b.compatibility],
    );

  const handleAddContent = (
    result: StudioContentSearchResult & { compatibility: CompatibilityLevel },
  ) => {
    if (result.compatibility === "conflicting" && onAddContentWithWarning) {
      onAddContentWithWarning(dayId, result, result.compatibility);
    } else {
      onAddContent(dayId, result);
    }
  };

  const previewItem = previewId
    ? rankedResults.find((r) => r.id === previewId)
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[420px] sm:w-[480px] p-0 flex flex-col"
      >
        <SheetHeader className="p-4 pb-0">
          <SheetTitle data-testid="text-drawer-title">
            Add Content to Day {dayNumber}
          </SheetTitle>
          <SheetDescription>
            Search and add content blocks to this day
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 space-y-3 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search content..."
              className="pl-8"
              data-testid="input-content-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger
                className="flex-1"
                data-testid="select-content-type-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="audio">Audio</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="blog">Blog</SelectItem>
                <SelectItem value="workbook">Workbook</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {effectiveLanguages.length > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40 p-2">
              <Globe className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Showing only content with{" "}
                {effectiveLanguages.length === 1 ? (
                  <span className="font-medium">
                    {getLanguageLabel(effectiveLanguages[0])}
                  </span>
                ) : (
                  <>
                    {effectiveLanguages.map((lang, i) => (
                      <span key={lang}>
                        {i > 0 &&
                          (i === effectiveLanguages.length - 1
                            ? " and "
                            : ", ")}
                        <span className="font-medium">
                          {getLanguageLabel(lang)}
                        </span>
                      </span>
                    ))}
                  </>
                )}{" "}
                language{" "}
                {effectiveLanguages.length === 1 ? "variant" : "variants"}
              </p>
            </div>
          )}
          {journeyTagOptionIds.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Results ranked by tag alignment with journey targeting
            </p>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-3 space-y-2">
            {isSearching && (
              <p className="text-xs text-muted-foreground text-center py-8">
                Searching...
              </p>
            )}
            {!isSearching && rankedResults.length === 0 && (
              <p
                className="text-xs text-muted-foreground text-center py-8"
                data-testid="text-no-results"
              >
                No content found
              </p>
            )}
            {rankedResults.map((result) => {
              const Icon = contentTypeIcons[result.contentType] || FileText;
              const isAlreadyInJourney = allContentIds.includes(result.id);
              const compat = compatConfig[result.compatibility];
              const CompatIcon = compat.icon;
              const isPreview = previewId === result.id;
              return (
                <div key={result.id} data-testid={`search-result-${result.id}`}>
                  <div
                    className={`flex items-start gap-3 p-3 rounded-md border text-sm hover-elevate cursor-pointer ${isPreview ? "ring-1 ring-primary/40" : ""}`}
                    onClick={() => setPreviewId(isPreview ? null : result.id)}
                  >
                    <div className="p-1.5 rounded-md bg-muted shrink-0">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm">
                        {result.title}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs capitalize">
                          {result.contentType}
                        </Badge>
                        {result.durationSeconds != null &&
                          result.durationSeconds > 0 && (
                            <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                              <Clock className="h-3 w-3" />
                              {formatDurationSeconds(result.durationSeconds)}
                            </span>
                          )}
                        {isAlreadyInJourney && (
                          <Badge variant="secondary" className="text-xs">
                            In Use
                          </Badge>
                        )}
                      </div>
                      {journeyTagOptionIds.length > 0 && (
                        <div
                          className={`flex items-center gap-1 mt-1.5 text-xs ${compat.color}`}
                        >
                          <CompatIcon className="h-3 w-3" />
                          <span>{compat.label}</span>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddContent(result);
                      }}
                      data-testid={`button-add-content-${result.id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {isPreview && previewItem && (
                    <div className="mt-1 ml-4 p-3 rounded-md border border-dashed space-y-2">
                      {previewItem.thumbnailUrl && (
                        <img
                          src={previewItem.thumbnailUrl}
                          alt={previewItem.title}
                          className="w-full h-28 object-cover rounded-md"
                        />
                      )}
                      {previewItem.description && (
                        <p className="text-xs text-muted-foreground">
                          {previewItem.description}
                        </p>
                      )}
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="outline" className="text-xs capitalize">
                          {previewItem.status}
                        </Badge>
                        {previewItem.language && (
                          <Badge variant="outline" className="text-xs">
                            {getLanguageLabel(previewItem.language)}
                          </Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          handleAddContent(previewItem);
                          setPreviewId(null);
                        }}
                        data-testid="button-add-from-preview"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add to Day {dayNumber}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
